import React, { useEffect, useState } from 'react';
import Carousel, { ArrowProps } from 'react-multi-carousel';
import Image from 'next/image';

import { ITestimonialSliderFields } from '../../../types/contentful';

import { GreenlightTheme } from '../../theme/themes/greenlight/index';
import { breakpoints } from '../../styles/media-queries';

import { Testimonial } from './Testimonial';
import { BlockContainer, Layout, Column } from '../Layout';
import { Text } from '../Text';

import {
  CarouselContainer,
  CustomArrow as StyledCustomArrow,
  Direction,
  BlockHeading,
  MobileContainer
} from './styles';

import angleLeft from '@/images/angle-left.svg';
import angleRight from '@/images/angle-right.svg';

import 'react-multi-carousel/lib/styles.css';

interface CustomArrowProps extends ArrowProps {
  icon: string;
  direction: Direction;
}

const colors = [
  GreenlightTheme.color.sliderSlide1,
  GreenlightTheme.color.sliderSlide2,
  GreenlightTheme.color.sliderSlide3
];

const CustomArrow = ({ onClick, direction, icon }: CustomArrowProps) => {
  return (
    <StyledCustomArrow direction={direction} onClick={onClick}>
      <Image src={icon} alt={`slider-arrow-${direction}`} />
    </StyledCustomArrow>
  );
};

export function TestimonialSliderBlock({
  testimonials,
  headingText
}: ITestimonialSliderFields) {
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    setIsMobile(
      typeof window !== `undefined` && window.innerWidth < breakpoints.tablet
    );
  }, []);

  return isMobile ? (
    <MobileContainer data-blockid="testimonial-slider">
      <BlockHeading
        as="h2"
        fontSize={{ 0: 'h2_0', desktop: 'h2_desktop' }}
        fontWeight="h2"
        color="defaultText"
      >
        {headingText ?? 'What parents are saying'}
      </BlockHeading>
      <CarouselContainer>
        <Carousel
          responsive={{
            s: {
              breakpoint: { max: breakpoints.tablet, min: 0 },
              items: 1
            },
            m: {
              breakpoint: {
                max: breakpoints.desktop,
                min: breakpoints.tablet + 1
              },
              items: 2
            }
          }}
          swipeable={false}
          draggable={false}
          infinite={true}
          customRightArrow={<CustomArrow direction="right" icon={angleRight} />}
          customLeftArrow={<CustomArrow direction="left" icon={angleLeft} />}
        >
          {testimonials!.map(({ fields }, i) => (
            <Testimonial {...fields} key={i} color={colors[i]} />
          ))}
        </Carousel>
      </CarouselContainer>
    </MobileContainer>
  ) : (
    <BlockContainer
      marginTop="blockMarginY_tablet"
      marginBottom="blockMarginY_tablet"
      data-blockid="testimonialSlider"
      data-testid="desktop-testimonial-slider"
    >
      <Layout gutter={{ 0: 's', max: 'l' }}>
        <Column size={12} justifyContent="center" marginBottom="m">
          <Text
            as="h2"
            fontSize={{ 0: 'h2_0', desktop: 'h2_desktop' }}
            fontWeight="h2"
            color="defaultText"
          >
            {headingText ?? 'What parents are saying'}
          </Text>
        </Column>
        {testimonials!.map(({ fields }, i) => (
          <Column size={4} key={i}>
            <Testimonial
              {...fields}
              color={colors[i]}
              data-testid="testimonial-block-component"
            />
          </Column>
        ))}
      </Layout>
    </BlockContainer>
  );
}
